/* eslint-disable react/no-unstable-nested-components */
import ChristTheRedeemer from "components/icons/ChristTheRedeemer";
import StatueOfLiberty from "components/icons/StatueOfLiberty";
import { PrimitiveSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from "components/ui/Select";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import aspectRatio from "components/utils/aspectRatio";
import { invariant } from "outvariant";
import { IPlatformVertical } from "types/graphqlTypes";

type OnlyExactVerticals = Extract<
  IPlatformVertical,
  IPlatformVertical.Acoes | IPlatformVertical.Stocks | IPlatformVertical.Reits | IPlatformVertical.Fiis
>;
interface SelectPlatformVerticalProps {
  value: IPlatformVertical;
  onChange: (value: OnlyExactVerticals) => void;
  disabled?: boolean;
  className?: string;
}
export default function SelectPlatformVertical({
  value,
  disabled = false,
  className,
  onChange,
}: SelectPlatformVerticalProps) {
  const theme = useTheme();
  const selectedOption = AVAILABLE_VERTICALS.find(({ vertical }) => value === vertical);
  invariant(selectedOption, `Invalid vertical: "%s"`, value);

  return (
    <Select value={value} disabled={disabled} onValueChange={(value) => onChange(value as OnlyExactVerticals)}>
      <SelectTrigger
        hideChevronIcon={true}
        className={classNames(
          "inline-flex",
          "border border-slate-200 bg-white lg:min-w-32 lg:max-w-[134px]",
          "font-aeonik text-sm font-medium tracking-wide text-slate-600",
          "hover:ring-0 focus-visible:ring-2",
          "hover:ring-offset-1 focus-visible:ring-offset-1",
          "hover:border-transparent hover:ring-slate-100 hover:ring-offset-slate-300",
          "focus-visible:border-transparent focus-visible:ring-indigo-100 focus-visible:ring-offset-indigo-300",
          "disabled:select-none",
          "truncate",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
          "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
          "rounded-xl",
          selectedOption.triggerClassName,
          className,
        )}
      >
        <SelectValue asChild={true}>
          <div className="pointer-events-none flex select-none items-center justify-start gap-x-2">
            {selectedOption.icon}

            <strong className={classNames("text-xs font-bold tracking-wide", selectedOption.labelClassName)}>
              {selectedOption.label}
            </strong>
          </div>
        </SelectValue>
      </SelectTrigger>

      <SelectContent
        side="bottom"
        scrollAreaViewportClassName="space-y-1"
        className={classNames("min-w-36 rounded-2xl border border-slate-300 text-sm", {
          "border-slate-900 bg-slate-700 text-slate-300 shadow-neutral-950/40": theme === "dark",
          dark: theme === "dark",
        })}
      >
        {AVAILABLE_VERTICALS.map(({ label, icon, vertical, itemClassName }) => {
          return (
            <PrimitiveSelectItem key={label} value={vertical} className={itemClassName}>
              <div className="flex w-full cursor-pointer items-center justify-start gap-x-2 rounded-xl py-1 text-sm">
                {icon}
                <h4 className="inline-flex text-xs font-bold dark:text-slate-300">{label}</h4>
              </div>
            </PrimitiveSelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

const AVAILABLE_VERTICALS = [
  {
    label: "Ações",
    vertical: IPlatformVertical.Acoes,
    // icon: <Twemoji emoji="🇧🇷" />,
    icon: (
      <ChristTheRedeemer
        className="inline-flex squircle"
        {...aspectRatio({ originalWidth: 512, originalHeight: 512, desiredWidth: 18 })}
      />
    ),
    triggerClassName: classNames(
      "border-sky-800/30 bg-sky-600/10",
      "hover:ring-sky-500/10 hover:ring-offset-sky-900/30",
      "focus-visible:ring-sky-500/10 focus-visible:ring-offset-sky-900/30",
    ),
    labelClassName: "text-sky-700 dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-sky-600/10 data-[state=checked]:text-sky-700 hover:bg-sky-300/10",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
  {
    label: "FIIs",
    vertical: IPlatformVertical.Fiis,
    // icon: <Twemoji emoji="🇺🇸" />,
    icon: (
      <ChristTheRedeemer
        className="inline-flex squircle"
        {...aspectRatio({ originalWidth: 390, originalHeight: 390, desiredWidth: 18 })}
      />
    ),
    triggerClassName: classNames(
      "border-slate-800/30 bg-slate-300/10",
      "hover:ring-slate-500/10 hover:ring-offset-slate-900/30",
      "focus-visible:ring-slate-500/10 focus-visible:ring-offset-slate-900/30",
    ),
    labelClassName: "text-slate-700 dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-slate-600/5 data-[state=checked]:text-slate-700 hover:bg-slate-300/10",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
  {
    label: "Stocks",
    vertical: IPlatformVertical.Stocks,
    // icon: <Twemoji emoji="🇧🇷" />,
    icon: (
      <StatueOfLiberty
        className="inline-flex squircle"
        {...aspectRatio({ originalWidth: 18, originalHeight: 18, desiredWidth: 18 })}
      />
    ),
    triggerClassName: classNames(
      "border-red-800/30 bg-red-200/10",
      "hover:ring-red-500/10 hover:ring-offset-red-900/30",
      "focus-visible:ring-red-500/10 focus-visible:ring-offset-red-900/30",
    ),
    labelClassName: "text-[#f42706] dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-red-600/5 data-[state=checked]:text-[#f42706] hover:bg-red-300/10",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
  {
    label: "REITs",
    vertical: IPlatformVertical.Reits,
    // icon: <Twemoji emoji="🇺🇸" />,
    icon: (
      <StatueOfLiberty
        className="inline-flex squircle"
        {...aspectRatio({ originalWidth: 18, originalHeight: 18, desiredWidth: 18 })}
      />
    ),
    triggerClassName: classNames(
      "border-emerald-800/30 bg-emerald-300/10",
      "hover:ring-emerald-500/10 hover:ring-offset-emerald-900/30",
      "focus-visible:ring-emerald-500/10 focus-visible:ring-offset-emerald-900/30",
    ),
    labelClassName: "text-emerald-700 dark:text-slate-300",
    itemClassName: classNames(
      "border border-transparent",
      "data-[state=checked]:bg-emerald-600/5 data-[state=checked]:text-emerald-700 hover:bg-emerald-300/10",
      "dark:data-[state=checked]:bg-slate-900/60 dark:data-[state=checked]:text-white",
      "dark:data-[state=checked]:border-slate-900",
    ),
  },
];
